import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import { useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import { Box, Container, Grid, Stack, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import { BlackCheckedIcon } from '../../../Common/_components/CheckBoxes';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 135;
const VIEW_PRODUCT_ID = 164;

export default function PetBowlsPage() {
  const [t] = useTranslation('page/products/pet-bowls');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Pet Bowls | Products')}
      description={t('Design and create your own custom printed Pet Bowls featuring your artwork. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#ffe8b5"
      bgStyle={{
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '55%',
      }}
      fullPageBg
      tag={t('The all-over print')}
      title={t('Pet Bowls')}
      text={t('Treat your furry friend to mealtime in style with our double-walled stainless steel pet bowls! Sturdy, drop‑resistant, and anti-slip, they stay put while pets eat. Available in 16 oz and 32 oz, each bowl can be customized with vibrant, long-lasting UV prints.')}
      createUrl={productData.get('url_slug')}
      img={require('@resources/img/pages/products/pet-bowls/banner-1-' + locale + '.png')}
      alt={t('Picture of Pet Bowls')}
      details={[
        {
          img: require('@resources/img/pages/products/pet-bowls/icon-1.svg?url'),
          alt: t('Food safe icon'),
          text: t('Made from food-safe stainless steel'),
        },
        {
          img: require('@resources/img/pages/products/pet-bowls/icon-2.svg?url'),
          alt: t('Insulation icon'),
          text: t('Insulated, double-walled design'),
        },
        {
          img: require('@resources/img/pages/products/pet-bowls/icon-3.svg?url'),
        alt: t('Rubber icon'),
          text: t('A non-slip rubber ring underneath prevents the bowl from moving'),
        },
        {
          img: require('@resources/img/pages/products/pet-bowls/icon-4.svg?url'),
          alt: t('Durable ink icon'),
          text: t('Durable and vibrant UV digital print'),
        },
        {
          img: require('@resources/img/pages/products/pet-bowls/icon-5.svg?url'),
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/pet-bowls/gallery_0.jpg'),
      require('@resources/img/pages/products/pet-bowls/gallery_1.jpg'),
      require('@resources/img/pages/products/pet-bowls/gallery_2.jpg'),
      require('@resources/img/pages/products/pet-bowls/gallery_3.jpg'),
      require('@resources/img/pages/products/pet-bowls/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your Pet Bowls<br/> <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love Pet Bowls! At prices designed for reselling, Pet Bowls made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      listOptions
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Pet Bowls Sizing')}
      text={<Trans
        t={t}
        i18nKey={tKey('Available in 2 sizes: 16 oz and 32 oz. The 16 oz bowl has a height of <smallHeight/> and a diameter of <smallDiameter/>, while the 32 oz bowl has a height of <largeHeight/> and a diameter of <largeDiameter/>.')}
        components={{
          smallHeight: <SizeDisplay sizes={[2.2]} sourceUnit="in" displayType="physical" />,
          smallDiameter: <SizeDisplay sizes={[5.5]} sourceUnit="in" displayType="physical" />,
          largeHeight: <SizeDisplay sizes={[2.8]} sourceUnit="in" displayType="physical" />,
          largeDiameter: <SizeDisplay sizes={[6.4]} sourceUnit="in" displayType="physical" />,
        }}
      />}
    >
      <img
        src={require('@resources/img/pages/products/pet-bowls/measurement.svg?url')}
        style={{
          marginRight: 0,
        }}
      />
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Pet Bowls')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Pet Bowls with these great products to create your own unique styles.')}
      productSlugs={['cotton-silk-bandana', 'coaster', 'stainless-steel-tumbler']}
    />
  </div>
}
